/**
 * Authentication helpers — wraps Firebase Auth with our error semantics.
 *
 * Signup now requires a unique handle (claimed atomically via the
 * `handles/` collection) plus a displayName. Google sign-in users that
 * land here without a handle are routed through `needsHandle()` so the
 * UI can prompt them to choose one before proceeding.
 */
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  type User,
  type Unsubscribe,
} from "firebase/auth";
import { isNativePlatform } from "../lib/platform";
import { doc, setDoc, serverTimestamp, Timestamp, getDoc, deleteDoc } from "firebase/firestore";

import { auth, db } from "../lib/firebase";
import { claimHandle, validateHandleFormat } from "./handles";
import type { CountryCode } from "../types";

/** Friendly error messages from Firebase auth + handle error codes. */
function describeError(err: unknown): string {
  const message = (err as Error)?.message ?? "";
  // Handle errors from our handles service are tagged with "handle:<reason>".
  if (message.startsWith("handle:")) {
    const reason = message.slice("handle:".length);
    switch (reason) {
      case "tooShort":
        return "Handle must be at least 3 characters.";
      case "tooLong":
        return "Handle must be 20 characters or fewer.";
      case "invalidChars":
        return "Handle can only contain lowercase letters, numbers, dots and underscores.";
      case "leadingDigit":
        return "Handle cannot start with a number.";
      case "edgeSeparator":
        return "Handle cannot start or end with `.` or `_`.";
      case "consecutiveDots":
        return "Handle cannot contain `..`.";
      case "taken":
        return "That handle is already taken — try another.";
      default:
        return "Invalid handle.";
    }
  }
  const code = (err as { code?: string })?.code ?? "";
  switch (code) {
    case "auth/email-already-in-use":
      return "This email is already registered. Try signing in instead.";
    case "auth/invalid-email":
      return "That email address looks invalid.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/user-not-found":
      return "No Acework account found for this email. If you just tried to sign up, the sign-up may have failed silently — please try signing up again and watch for a red error message.";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Email or password is incorrect.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a minute and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return message || "Something went wrong. Please try again.";
  }
}

export type SignUpInput = {
  email: string;
  password: string;
  handle: string;
  displayName: string;
  country?: CountryCode;
  city?: string;
  /** yyyy-mm-dd. Required for new signups per compliance flow. */
  birthdate?: string;
  /** Version of the ToS the user checked "I agree" against. */
  tosVersion?: string;
  /** Optional CASL marketing/community message opt-in. */
  marketingOptIn?: boolean;
  /** Optional self-declared gender. "X" = prefer not to say (default).
   *  Written to profile.gender so downstream (Roundup card, tournament
   *  balancing, etc.) picks it up without a follow-up backfill. */
  gender?: "M" | "F" | "X";
};

export async function signUpWithEmail(input: SignUpInput): Promise<User> {
  // Validate handle format BEFORE creating the auth account so we don't end up
  // with a registered Firebase user that has no profile.
  const v = validateHandleFormat(input.handle);
  if (!v.ok) {
    throw new Error(describeError(new Error("handle:" + v.reason)));
  }

  try {
    const { user } = await createUserWithEmailAndPassword(
      auth,
      input.email,
      input.password,
    );
    try {
      // Atomically claim the handle. Throws if taken.
      await claimHandle(input.handle, user.uid);

      await updateProfile(user, { displayName: input.displayName });

      const handle = input.handle.trim().toLowerCase();
      const displayName = input.displayName.trim();
      const city = input.city?.trim();
      const profileData: Record<string, unknown> = {
        id: user.uid,
        handle,
        handleLower: handle,
        displayName,
        displayNameLower: displayName.toLowerCase(),
        email: user.email,
        photoURL: user.photoURL ?? null,
        tier: "elite", // 90-day Elite trial for new sign-ups
        tierExpiresAt: Timestamp.fromMillis(Date.now() + 90 * 24 * 60 * 60 * 1000),
        createdAt: serverTimestamp(),
      };
      
      if (input.country) {
        profileData.country = input.country;
      }
      if (city) {
        profileData.city = city;
        profileData.cityLower = city.toLowerCase();
      }

      // Legal / compliance snapshot — ToS acceptance is required; marketing
      // opt-in and birthdate are optional at the DB level but the signup UI
      // enforces them. We deliberately store `tosAcceptedAt` only when we
      // also have a version string, so a partially-migrated profile can be
      // detected via `!tosAcceptedAt`.
      if (input.tosVersion) {
        profileData.tosAcceptedAt = serverTimestamp();
        profileData.tosVersion = input.tosVersion;
      }
      if (typeof input.marketingOptIn === "boolean") {
        profileData.marketingOptIn = input.marketingOptIn;
        if (input.marketingOptIn) {
          profileData.marketingOptInAt = serverTimestamp();
        }
      }
      if (input.birthdate) {
        profileData.birthdate = input.birthdate;
      }
      // Always stamp gender (defaults to "X") so the Home backfill
      // picker never fires for a new signup. "X" is a legit choice
      // ("prefer not to say") and stays hidden on the card.
      profileData.gender = input.gender ?? "X";

      await setDoc(doc(db, "profiles", user.uid), profileData);
      return user;
    } catch (innerErr) {
      // If handle claim failed, roll back the auth account so the user
      // can retry without "email already in use" errors.
      await user.delete().catch(() => {});
      throw innerErr;
    }
  } catch (err) {
    throw new Error(describeError(err));
  }
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  try {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    return user;
  } catch (err) {
    throw new Error(describeError(err));
  }
}

export async function signInWithGoogle(): Promise<User | null> {
  // Stub — disabled until native plugin issues are resolved.
  // Login page hides the Google button; this is kept only to satisfy imports.
  throw new Error("Google sign-in is temporarily disabled.");
}

/**
 * Should be called once on app boot. Returns the User if the app just came
 * back from a Google redirect sign-in, otherwise null.
 */
export async function consumeGoogleRedirect(): Promise<User | null> {
  try {
    const result = await getRedirectResult(auth);
    return result?.user ?? null;
  } catch (err) {
    throw new Error(describeError(err));
  }
}

/**
 * Sign in with Apple — uses the native Apple ID sheet on iOS via the
 * @capacitor-firebase/authentication plugin, falls back to Firebase web
 * popup on browser builds.
 *
 * Like Google sign-in, first-time users land WITHOUT a profile doc — the
 * UI routes them through `needsHandle()` to claim a handle.
 */
export async function signInWithApple(): Promise<User | null> {
  // Stub — disabled until App Store submission. Login page hides the button.
  throw new Error("Apple sign-in is temporarily disabled.");
}

/** Has this user completed handle setup yet? Used by routing guards. */
export async function needsHandle(uid: string): Promise<boolean> {
  const snap = await getDoc(doc(db, "profiles", uid));
  if (!snap.exists()) return true;
  const data = snap.data() as { handle?: string };
  return !data.handle;
}

/** Finalise a Google-auth user by giving them a handle + displayName. */
export async function completeProfileSetup(
  user: User,
  handle: string,
  displayName: string,
): Promise<void> {
  const v = validateHandleFormat(handle);
  if (!v.ok) {
    throw new Error(describeError(new Error("handle:" + v.reason)));
  }
  try {
    await claimHandle(handle, user.uid);
    await updateProfile(user, { displayName });
    const handleLc = handle.trim().toLowerCase();
    await setDoc(
      doc(db, "profiles", user.uid),
      {
        id: user.uid,
        handle: handleLc,
        handleLower: handleLc,
        displayName: displayName.trim(),
        displayNameLower: displayName.trim().toLowerCase(),
        email: user.email,
        photoURL: user.photoURL ?? null,
        createdAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (err) {
    throw new Error(describeError(err));
  }
}

export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}

/**
 * Permanently delete the user's account and all associated data.
 *
 * Order:
 *   1. Delete profile + handle docs in Firestore (best-effort cleanup).
 *   2. Delete the Firebase Auth user.
 *
 * App Store Review Guideline 5.1.1(v) requires apps that allow account
 * creation to also offer in-app deletion. The user is signed out
 * automatically after deletion.
 *
 * NOTE: This is a one-way operation. Tournament participation, match
 * history, etc. are NOT cascaded — they keep their stable uid references
 * but the user's profile / handle are gone.
 */
export async function deleteAccount(): Promise<void> {
  const current = auth.currentUser;
  if (!current) throw new Error("Not signed in");

  const uid = current.uid;
  try {
    // Read profile to know which handle doc to clear.
    const profileSnap = await getDoc(doc(db, "profiles", uid));
    const handle: string | undefined = profileSnap.data()?.handle;

    // Best-effort cleanup. We swallow errors so deletion still proceeds
    // even if a write fails — the auth account being gone is the
    // important part for compliance.
    await Promise.allSettled([
      deleteDoc(doc(db, "profiles", uid)),
      handle ? deleteDoc(doc(db, "handles", handle)) : Promise.resolve(),
    ]);

    // Finally, delete the auth user. This may require a recent sign-in;
    // if Firebase returns `requires-recent-login`, we surface that.
    await current.delete();
  } catch (err) {
    const code = (err as { code?: string })?.code ?? "";
    if (code === "auth/requires-recent-login") {
      throw new Error(
        "For security, please sign out and sign back in, then try deleting your account again.",
      );
    }
    throw new Error(describeError(err));
  }
}

/**
 * Send a password-reset email.
 *
 * By default Firebase renders its OWN reset UI at
 * `{authDomain}/__/auth/action?mode=resetPassword&oobCode=...` and
 * the user sets a new password there. We pass an ActionCodeSettings
 * so that after a successful reset the "Continue" button on Firebase's
 * page takes them back to /login on our site instead of dead-ending.
 *
 * The `handleCodeInApp: false` is critical — we want Firebase to host
 * the reset form itself, NOT hand the oobCode back to our SPA (which
 * would need its own /reset-password page and mangled URL handling).
 * The friends who reported "email link does nothing" were most likely
 * hitting the SPA-hosted `/__/auth/action` catch-all rewrite, which
 * silently renders Home. Locking the URL down to firebaseapp.com plus
 * an explicit continueUrl makes the flow deterministic.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email.trim(), {
      url: "https://acework.app/login",
      handleCodeInApp: false,
    });
  } catch (err) {
    throw new Error(describeError(err));
  }
}

export function observeAuthState(cb: (user: User | null) => void): Unsubscribe {
  return onAuthStateChanged(auth, cb);
}

export type { User };
